<template>
  <div class="ui top basic segment">
    <h3 class="ui horizontal left aligned divider header capitalize-fl">
      {{ $tc('EXTENSION', 2) | capitalize }}
    </h3>

    <div v-html="$t('EXTENSIONS_INPUT_DESCRIPTION')" />

    <spinny :promise="loading"></spinny>

    <div class="ui basic segment">
      <div class="ui two column grid">
        <template v-for="(e, idx) in $v.flowExtensions.$each.$iter">
          <div class="two column row" :key="e.$model.name + '_' + e.$model.type + '_header'">
            <h4
              class="ui horizontal left aligned divider header capitalize-fl"
              style="width: 100%;"
              v-html="$t('EXTENSION_HEADER', { name: e.$model.name, type: e.$model.type })"
            ></h4>
          </div>

          <div class="row" :key="e.$model.name + '_' + e.$model.type + '_content'">
            <!-- Description -->
            <div class="column">
              <div
                class="markdown extension description"
                v-html="
                  renderMarkdown(
                    descriptions[e.$model.type] && descriptions[e.$model.type][e.$model.name]
                  )
                "
              ></div>
            </div>
            <!-- STRING -->
            <div class="input column" v-if="e.$model.type === 'string'">
              <div
                class="content field"
                :style="{ zIndex: 1000 - idx }"
                :class="{ error: e.$error }"
              >
                <label :for="e.$model.name">
                  {{ $t('EXTENSION_STRING_INPUT_FOR', { name: e.$model.name }) }}
                </label>
                <input :id="e.$model.name" type="text" v-model.trim="e.$model.value" />
              </div>
            </div>
            <!-- SELECTION -->
            <div class="input column" v-else-if="e.$model.type === 'selection'">
              <div class="content field" :style="{ zIndex: 1000 - idx }">
                <dropdown-select
                  :labelText="$t('EXTENSION_SELECTION_INPUT_FOR', { name: e.$model.name })"
                  :initialSelection="e.$model.value"
                  :optional="true"
                  :emitInitialSelection="false"
                  :items="e.$model.options"
                  @selected="selectOption($event, e.$model)"
                />
              </div>
            </div>
            <!-- FLOW -->
            <div class="input column" v-else-if="e.$model.type === 'flow'">
              <div class="content field" :style="{ zIndex: 1000 - idx }">
                <typeahead
                  :multiple="false"
                  :current-item="nameOfFlow(e.$model.value)"
                  :items="flows | withIO(e.$model.inputs)"
                  :label-text="
                    $t('EXTENSION_FLOW_INPUT_FOR', {
                      name: e.$model.name,
                      inputs:
                        e.$model.inputs && e.$model.inputs.length > 0
                          ? $t('INPUTS', { list: e.$model.inputs.join(', ') })
                          : $t('NO_INPUTS')
                    })
                  "
                  :placeholder-text="$tc('FLOW', 1)"
                  :empty-text="$t('NO', { things: $tc('FLOW', 1) })"
                  @selected="flowSelected($event, e.$model)"
                  :single-item-formatter="nameOfFlow"
                >
                  <template slot="dropdown" slot-scope="props">
                    {{ props.item.name }}
                    <span class="subtle">{{ nameOfApp(props.item.application) }}</span>
                  </template>
                </typeahead>
              </div>
            </div>
            <!-- FIELD -->
            <div class="input column" v-else-if="e.$model.type === 'field'">
              <div class="content field" :style="{ zIndex: 1000 - idx }">
                <typeahead
                  :multiple="false"
                  :current-item="nameOfField(e.$model.value)"
                  :items="fields"
                  :label-text="$t('EXTENSION_FIELD_INPUT_FOR', { name: e.$model.name })"
                  :placeholder-text="$tc('FIELD', 1)"
                  :empty-text="$t('NO', { things: $tc('FIELD', 1) })"
                  @selected="fieldSelected($event, e.$model)"
                  :single-item-formatter="nameOfField"
                >
                  <template slot="dropdown" slot-scope="props">
                    {{ props.item.name }}
                    <span class="subtle">{{ nameOfApp(props.item.application) }}</span>
                  </template>
                </typeahead>
              </div>
            </div>
            <!-- TABLE -->
            <div class="input column" v-else-if="e.$model.type === 'table'">
              <div class="content field" :style="{ zIndex: 1000 - idx }">
                <typeahead
                  :multiple="false"
                  :current-item="e.$model.value"
                  :items="tables"
                  :label-text="$t('EXTENSION_TABLE_INPUT_FOR', { name: e.$model.name })"
                  :placeholder-text="$tc('TABLE', 1) | capitalize"
                  :empty-text="$t('NO', { things: $tc('TABLE', 1) })"
                  @selected="tableSelected($event, e.$model)"
                >
                  <template slot="dropdown" slot-scope="props">
                    {{ props.item.name }}
                  </template>
                </typeahead>
              </div>
            </div>
            <div class="input column" v-else>
              <div class="content">{{ e.$model.type }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.top.basic.segment {
  overflow-y: auto;
  height: 100%;
}

.subtle {
  color: gray;
}

.input.column {
  .content {
    position: sticky;
    top: 20px;
    margin-top: 1em;
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import { mapFields, mapMultiRowFields } from 'vuex-map-fields'
import { Hub, Flow, Application, Field, Table } from 'services'
import { Spinny, DropdownSelect, Typeahead } from 'ui'

import marked from 'marked'
import DOMPurify from 'dompurify'
import hljs from 'highlight.js'
import 'highlight.js/styles/github.css'

const markedOptions = {
  highlight: function(code, lang) {
    const language = hljs.getLanguage(lang) ? lang : 'plaintext'
    return hljs.highlight(code, { language }).value
  },
  smartypants: true,
  smartLists: true
}

function valueValidation(e) {
  if (e.type === 'string' && e.value && e.shouldMatch) {
    return !_.isNil(e.value.match(e.shouldMatch))
  }

  return true
}

export default {
  name: 'flow-extensions',

  components: { Spinny, DropdownSelect, Typeahead },

  computed: {
    ...mapFields('flowEditor', {
      flow: 'flow',
      flowId: 'flow.id'
    }),
    ...mapMultiRowFields('flowEditor', {
      flowExtensions: 'flow.extensions'
    }),
    flows() {
      return this.$store.state.flow.items
    },
    fields() {
      return _.filter(this.$store.state.field.items, f => f.application === this.flow.application)
    },
    apps() {
      return this.$store.state.application.items
    },
    tables() {
      return this.$store.state.table.items
    }
  },

  validations: {
    flowExtensions: {
      $each: {
        valueValidation
      }
    }
  },

  data() {
    return {
      loading: null,
      pub: {},
      extensions: this.flowExtensions,
      descriptions: {}
    }
  },

  filters: {
    withIO: function(flows, inputs) {
      return _.filter(flows, f => {
        return (
          _.isEmpty(inputs) ||
          _.intersection(_.map(f.inputs, 'name'), inputs).length == inputs.length
        )
      })
    }
  },

  methods: {
    renderMarkdown(md) {
      const options = { ...markedOptions }
      if (md) return DOMPurify.sanitize(marked(md, options))
    },
    selectOption(evt, extension) {
      this.$set(extension, 'value', evt?.id)
    },

    flowSelected(evt, extension) {
      this.$set(extension, 'value', evt?.id)
    },
    nameOfFlow(flowId) {
      return _.find(this.flows, f => f.id === flowId)?.name || ''
    },

    fieldSelected(evt, extension) {
      this.$set(extension, 'value', evt?.id)
    },
    nameOfField(fieldId) {
      return _.find(this.fields, f => f.id === fieldId)?.name || ''
    },

    tableSelected(evt, extension) {
      this.$set(extension, 'value', evt?.name)
    },

    nameOfApp(appId: string): string {
      let app = _.find(this.apps, app => app.id === appId)
      return (app && app.name) || ''
    }
  },

  watch: {
    '$v.$error': function(validationError) {
      // emit this st the FlowEditor can incorporate the validation here in
      // its own validation scheme to disable save etc
      this.$emit('validates', !validationError)
    }
  },

  mounted() {
    this.loading = Hub.connections().then(conns => {
      return Promise.all(
        _.flatMap(conns, conn =>
          Hub.metaFor(conn, this.flow?.publication, this.flow?.publication?.releaseNote?.version)
        )
      ).then(meta => {
        this.pub = meta[0]?.body?.item || {}

        this.descriptions = _.reduce(
          this.pub.extensions,
          (memo, e) => {
            _.set(memo, [e.type, e.name], e.description)
            return memo
          },
          {}
        )
      })
    })
    Promise.all([
      Flow.readAllFlows(),
      Application.readAllApps(),
      Field.readFields(),
      Table.readAllTables()
    ]).then(this.$v.$touch)
  }
}
</script>
